import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo, } from "react";
import { bookoraAntdTheme } from "@bookora/design/antd";
import { App as AntdApp, ConfigProvider, theme as antdTheme } from "antd";
import faIR from "antd/locale/fa_IR";
import { resolveCornerPlacement, } from "../antd/direction.js";
import { TooltipGroupProvider } from "../antd/tooltip-context.js";
import { DEFAULT_TOOLTIP_TIMING } from "../antd/tooltip-group.js";
import { Icon } from "../icons/icon.js";
import { useThemeMode, } from "./theme-mode.js";
/**
 * The direction every AntD component in both panels renders in.
 *
 * Named rather than written inline because it is read TWICE in this file — once
 * by `ConfigProvider`, once to resolve the toast corner below — and two copies
 * of a direction is how a subtree ends up disagreeing with its own provider.
 */
const PANEL_DIRECTION = "rtl";
/**
 * ── THE LAST PHYSICAL PLACEMENT IN THE KIT ──────────────────────────────────
 *
 * This was `placement: 'topLeft'`, hardcoded, in the one file whose entire
 * premise (`antd/direction.ts`, `antd/surfaces.tsx`) is that a physical
 * placement name must not be reachable — and where every overlay wrapper next
 * to it makes exactly this a type error.
 *
 * It was not wrong on screen. `antd/es/notification/util.js` maps `topLeft` to
 * a literal `{ left: 0 }` and **never reads `direction`**, so in these RTL
 * panels the toast landed at the inline END, which is the mirror of AntD's own
 * LTR default (`topRight`) and therefore the right corner — someone flipped it
 * by hand and got the right answer. That is the "right answer for the wrong
 * reason" the `Drawer` docblock in `antd/surfaces.tsx` calls out by name: it
 * carried no reason, nothing tested it, and it would have inverted silently the
 * first time any part of this rendered LTR.
 *
 * `top-end` states the intent. In RTL it resolves to `topLeft` — the same
 * pixels as before, so this is not a visual change — and in LTR to `topRight`,
 * which is where AntD would have put it anyway.
 *
 * Why the END and not the reading start: the reading start in RTL is the right,
 * and that is where the navigation rail is. A toast anchored there covers the
 * nav for its four seconds. The end corner is the only free one.
 */
const TOAST_PLACEMENT = "top-end";
/**
 * ── TOOLTIPS ANTD RENDERS ON ITS OWN ────────────────────────────────────────
 *
 * The kit `Tooltip` takes its timing from the delay group mounted below. AntD
 * also renders tooltips of its own that never pass through the kit — the
 * collapsed Menu rail, Typography's copy button, a Slider's value — and those
 * read this config. Same cold delay and hover-out grace as the kit, so a
 * sidebar rail does not flash at 100 ms next to a toolbar that waits 600.
 */
const ANTD_TOOLTIP_CONFIG = {
    mouseEnterDelay: DEFAULT_TOOLTIP_TIMING.openDelayMs / 1000,
    mouseLeaveDelay: DEFAULT_TOOLTIP_TIMING.closeDelayMs / 1000,
};
const ThemeContext = createContext(null);
/**
 * ── ALERT'S STATUS GLYPHS ───────────────────────────────────────────────────
 *
 * `<Alert showIcon>` drew `@ant-design/icons` glyphs whose accessible names
 * were their English file names — a screen reader announced «check-circle» /
 * «exclamation-circle» before every Persian message — and they were the one
 * standing exception to the single icon set (`docs/DECISIONS.md`), because
 * AntD 5 had no seam for them. AntD 6's `ConfigProvider.alert` has one, so
 * every Alert in both panels now draws registry glyphs, decorative (the
 * message beside them says what they mean), with a Persian close button.
 *
 * 20px: between AntD's 14px inline icon and its 24px description icon, and on
 * the registry's size ladder.
 */
const ALERT_CONFIG = {
    successIcon: _jsx(Icon, { name: "check", size: 20 }),
    infoIcon: _jsx(Icon, { name: "info", size: 20 }),
    warningIcon: _jsx(Icon, { name: "warning", size: 20 }),
    errorIcon: _jsx(Icon, { name: "close", size: 20 }),
    closeIcon: _jsx(Icon, { name: "close", size: 16, label: "\u0628\u0633\u062A\u0646" }),
};
/**
 * RTL + fa-IR + the Bookora AntD theme, in one place for both panels.
 *
 * ── WHY THIS IS THE ONLY `ConfigProvider` ───────────────────────────────────
 *
 * `direction="rtl"` is not a style, it is the input every AntD component reads
 * to decide which class names to paint and which default anchor to use. A
 * second `ConfigProvider` mounted inside a screen — the usual reason being "I
 * just wanted a different `componentSize`" — inherits *some* of the parent
 * config and resets the rest, and the subtree silently goes LTR. So the kit
 * does not export `ConfigProvider` at all (see `antd/index.ts`), and this is
 * the single mount point.
 *
 * ── PORTALS ─────────────────────────────────────────────────────────────────
 *
 * AntD renders Select dropdowns, Dropdown menus, Tooltips, Popovers, Modals
 * and Drawers into `document.body`. Two separate things have to be right and
 * they have different mechanisms:
 *
 *   1. **AntD's own direction** — carried by React context, which crosses
 *      portals, so every popup gets its `-rtl` class no matter how deeply
 *      nested the portal is. Pinned in `test/antd-rtl.test.tsx` for a Select
 *      inside a Drawer and a Dropdown inside a Modal.
 *   2. **The CSS direction of the raw markup inside those popups** — inherited
 *      through the DOM, from `<body>`, from `<html dir>`. `useThemeMode` sets
 *      that attribute; it is not decoration, it is what makes a plain `<div>`
 *      in a portal lay out RTL.
 *
 * `AntdApp` is mounted here rather than per-page because `message`/`modal`/
 * `notification` called from a bare static import miss all of the above — no
 * theme, no `fa-IR`, and no direction, which puts the OK button of a
 * destructive confirm on the wrong side.
 */
export function BookoraThemeProvider({ children, storageKey, themeOverride, }) {
    const theme = useThemeMode(storageKey);
    const config = useMemo(() => {
        const base = bookoraAntdTheme(theme.mode);
        return themeOverride ? themeOverride(base, theme.mode) : base;
    }, [theme.mode, themeOverride]);
    return (_jsx(ThemeContext.Provider, { value: theme, children: _jsx(ConfigProvider, { direction: PANEL_DIRECTION, locale: faIR, theme: config, 
            // AntD's own "compact" is a different density contract from the token
            // set; the panels get their density from `bookoraAntdTheme` instead.
            componentSize: "middle", alert: ALERT_CONFIG, tooltip: ANTD_TOOLTIP_CONFIG, 
            // Toast status glyphs are set per call by the kit's `App.useApp()`
            // (`antd/app.tsx`); the close button is the one piece with a seam here.
            notification: {
                closeIcon: _jsx(Icon, { name: "close", size: 16, label: "\u0628\u0633\u062A\u0646" }),
            }, form: {
                validateMessages: { required: "این قسمت رو تکمیل کنید." },
                // Set here rather than on a `Form` wrapper: a wrapper only reaches
                // the forms that remember to use it, and a long clinic form that
                // fails validation above the fold otherwise looks like it ignored
                // the submit.
                scrollToFirstError: { behavior: "smooth", block: "center" },
            }, children: _jsx(AntdApp, { notification: {
                    placement: resolveCornerPlacement(TOAST_PLACEMENT, PANEL_DIRECTION),
                    duration: 4,
                }, message: { duration: 3, maxCount: 3 }, children: _jsx(TooltipGroupProvider, { children: children }) }) }) }));
}
export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx)
        throw new Error("useTheme must be used inside <BookoraThemeProvider>");
    return ctx;
}
/** AntD's design tokens for the current theme — for charts and inline styles. */
export function useDesignTokens() {
    return antdTheme.useToken().token;
}
//# sourceMappingURL=theme-provider.js.map